import React, { useEffect, useMemo, useState, useImperativeHandle, useRef } from 'react';
import {Empty, Alert, Button, Flex, Space, Checkbox} from 'antd';
import useWebSocket from 'react-use-websocket';
import {Attachments, Bubble, Sender} from '@ant-design/x';
import {marked} from 'marked';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {PlusOutlined, SendOutlined} from '@ant-design/icons';
import { AnimatePresence } from 'framer-motion';
import parse, { domToReact } from 'html-react-parser';
import {
  StyledAgentChat,
  StyledAgentChatBody,
  StyledAgentChatBodyWelcome,
  StyledAgentChatBodyWelcomeTitle,
  StyledAgentChatConfirmation,
  StyledAgentChatConfirmationOption,
  StyledAgentChatDrawer,
  StyledAgentChatFooter,
  StyledAgentChatWaitingSpaceContainer,
  StyledAgentChatWaitingSpace,
  StyledAgentChatOptionButton,
} from './StyledAgentChat';
import Spin from '../Spin';
import {fileHelpers, systemHelpers, textHelpers} from '../../utils/helpers';
import {
  agentWebsocketActionsConstants,
  agentWebsocketFlowConstants,
} from '../../constants';
import {CheckIcon, CloseIcon, StarsIcon} from '../../icons';
import ScaleFade from '../animations/ScaleFade';

const defaultOptions = systemHelpers.getWebsocketOptions();

const roles = {
  assistant: { placement: 'start' },
  user: { placement: 'end' },
};

const AgentChat = React.forwardRef(({
  flow,
  footerHelper,
  skipInitialMessage = false,
  open,
  onClose,
  onUserMessage,
  welcomeText,
  query = {},
  ...rest
}, ref) => {
  const [isOpenConnection, setIsOpenConnection] = useState(false);
  const [chatDetails, setChatDetails] = useState({
    started: false,
    session_id: null,
  });
  const [loading, setLoading] = useState(false);
  const [t] = useTranslation(['main', 'agentChat']);

  const name = useSelector(state => textHelpers.getNameParts(state?.user?.user?.full_name).firstName)

  const {companyId, token} = useMemo(() => systemHelpers.getAuthDetails(), []);

  const socketUrl = useMemo(() => {
    if (!companyId) return null;
    let url = `${systemHelpers.getWebsocketUrl('agentChat')}/${companyId}/${flow}/`;
    if (Object.keys(query).length > 0) {
      const params = new URLSearchParams(query).toString();
      url += `?${params}`;
    }
    return url;
  }, [companyId, flow, query]); // eslint-disable-line react-hooks/exhaustive-deps

  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [input, setInput] = useState('');
  // Track whether we've already shown the welcome message in the chat
  const [welcomeShown, setWelcomeShown] = useState(false);

  const chatBodyRef = useRef(null);
  const fileInputRef = useRef(null);
  const senderRef = useRef(null);
  const userMessageKeyRef = useRef(0);

  // Determine if input should be disabled (loading or latest message is options)
  const isInputDisabled = useMemo(() => {
    const latestMessage = messages[messages.length - 1];
    const isOptionsType = latestMessage && (['options', 'confirmation'].includes(latestMessage.type) || latestMessage?.options) && latestMessage?.options?.length > 0;
    return loading || isOptionsType;
  }, [loading, messages]);

  // Helper to create message objects
  const createMessage = ({ type = 'text', message = '', options, role = 'assistant', file }) => ({
    id: `${Date.now()}-${type}-${Math.random()}`,
    role,
    type,
    message,
    options,
    file,
  });

  // When drawer opens, add the welcomeText as the first assistant message (once)
  useEffect(() => {
    if (open && welcomeText && !welcomeShown) {
      // Only add string messages. If welcomeText is not a string, convert to string as fallback.
      const text = typeof welcomeText === 'string' ? welcomeText : String(welcomeText);
      const welcomeMsg = createMessage({ message: text, role: 'assistant' });
      setMessages(prev => [welcomeMsg, ...prev]);
      setWelcomeShown(true);
    }
  }, [open, welcomeText, welcomeShown]);

  useEffect(() => {
    if (open && !isOpenConnection) {
      setIsOpenConnection(true);
    }
  }, [isOpenConnection, open]);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  const { getWebSocket, lastJsonMessage, readyState, sendJsonMessage } = useWebSocket(
    socketUrl,
    {
      ...defaultOptions,
      protocols: ['bearer', token],
      share: true,
      onClose: () => setIsOpenConnection(false),
      onError: () => setError('Connection error. Please try again.'),
    },
    isOpenConnection && Boolean(socketUrl),
  );

  const clearState = () => {
    if (chatDetails.started) setChatDetails({ started: false, session_id: null });
    if (error) setError(null);
    if (messages.length) setMessages([]);
    if (input) setInput('');
    if (loading) setLoading(false);
    if (userMessageKeyRef.current !== 0) userMessageKeyRef.current = 0;
    // Ensure welcome message can be shown again on next open
    if (welcomeShown) setWelcomeShown(false);
  }

  const getActionName = () => {
    return chatDetails.started ? agentWebsocketActionsConstants.CONTINUE_CHAT : agentWebsocketActionsConstants.NEW_CHAT;
  }

  useEffect(() => {
    if (!open && isOpenConnection) {
      getWebSocket()?.close();
    }
  }, [open, readyState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open && getWebSocket) {
      try { getWebSocket()?.close(); } catch (_) {}
    }
    if (!open) {
      setTimeout(clearState, 300); // Delay to allow any final messages to be processed
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!lastJsonMessage) return;

    // Handle status-based responses
    if (lastJsonMessage.status === 'success') {
      onClose?.({confirmed: true, flow, query});
      return;
    }
    if (lastJsonMessage.status === 'error') {
      setError(lastJsonMessage.message);
      return;
    }

    const { is_data_collected, error, message, session_id, options } = lastJsonMessage || {};

    if (error) {
      setMessages(prev => ([
        ...prev,
        createMessage({
          type: 'error',
          message: t(`agentChat:errorCodes.${error}`),
        })
      ]));
      setLoading(false);
      return;
    }

    if (is_data_collected) {
      setLoading(false);
      onClose?.({confirmed: true});
      return;
    }

    if (!(skipInitialMessage && !chatDetails.started)) {
      const messageObject = options
        ? createMessage({ message, options, type: 'options' })
        : createMessage({ message });
      setMessages([...messages, messageObject]);
    }

    if (chatDetails.session_id !== session_id) {
      setChatDetails({ started: true, session_id });
    }
    setLoading(false);
  }, [lastJsonMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = (payload) => {
    const text = typeof payload === 'string' ? payload : (payload?.text ?? payload?.message ?? '');
    if (!text) return;
    const messageKey = userMessageKeyRef.current;
    userMessageKeyRef.current += 1;
    onUserMessage?.({ message: text, messageKey });
    setLoading(true);
    setMessages(prev => ([...prev, createMessage({ message: text, role: 'user' })]));
    sendJsonMessage({
      action: getActionName(),
      body: {
        ...query, message: text, ...(chatDetails.started && chatDetails.session_id ? { session_id: chatDetails.session_id } : {})
      }
    });
  };

  useImperativeHandle(ref, () => ({
    sendMessage: handleSend
  }));

  // Helper to focus the sender input
  const focusSender = () => {
    if (senderRef.current && senderRef.current.focus) {
      senderRef.current.focus();
    }
  };

  useEffect(() => {
    focusSender();
  }, [messages]);

  useEffect(() => {
    if (open) {
      focusSender();
    }
  }, [open]);

  const onSubmit = () => {
    const text = input.trim();
    if (!text) return;
    handleSend(text);
    setInput('');
    focusSender();
  }

  const parseMessage = (msg) => {
    const html = marked(msg || '');
    return parse(html, {
      replace: (domNode) => {
        if (domNode && domNode.type === 'tag' && domNode.name === 'a') {
          const attribs = domNode.attribs || {};
          // Preserve existing attributes but enforce safe new-window behavior
          const props = {
            ...attribs,
            target: '_blank',
            rel: attribs.rel ? `${attribs.rel} noopener noreferrer` : 'noopener noreferrer',
          };
          return (
            <a {...props}>{domToReact(domNode.children || [])}</a>
          );
        }
        return undefined;
      },
    });
  };

  const getWaitingSpinner = (type) => (
    <ScaleFade>
      <StyledAgentChatWaitingSpace direction='horizontal' size={4} type={type}>
        <Spin size='xxsmall' />
        <span>{t('waitASec')}...</span>
      </StyledAgentChatWaitingSpace>
    </ScaleFade>
  );

  const handlePlusClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const base64 = await fileHelpers.getBase64(file);
      sendJsonMessage({
        action: getActionName(),
        body: {
          ...query,
          session_id: chatDetails.session_id,
          message: '',
          attachment_base64: base64,
          attachment_filename: file.name
        }
      });
      setMessages(prev => ([
        ...prev,
        {
          id: `${Date.now()}-${prev.length}`,
          role: 'user',
          type: 'file',
          file: {
            name: file.name,
            size: file.size,
            type: file.type,
          }
        }
      ]));
    } catch (err) {
      setError('Failed to upload file.');
    }
    setLoading(false);
    // Reset file input so the same file can be uploaded again if needed
    e.target.value = '';
  };

  // Add state for selected options
  const [selectedOptions, setSelectedOptions] = useState([]);

  const toggleOption = (value) => {
    setSelectedOptions((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );
  };

  const submitOptions = (options) => {
    const text = options.join(', ');
    handleSend(text);
    setSelectedOptions([]);
  };

  const renderOptions = ({message, options} = {}) => {
    let subOptions = typeof options === 'object' && Array.isArray(options.values) ? options.values : [];
    const optionsType = options?.type;
    const isMultiple = optionsType === 'multiple';
    const isOptional = options?.optional || false;
    const fieldName = options?.field;

    return (
      <StyledAgentChatConfirmation>
        <p>{parseMessage(message)}</p>
        <Space direction='vertical' className='w-100'>
          <Space direction='vertical' className='w-100'>
            {subOptions.map((value) => {
              const valueKey = value.toLowerCase();
              const label = typeof value === 'string' && value.length ? value[0].toUpperCase() + value.slice(1) : value;
              const variant = ['confirm', 'cancel'].includes(valueKey) ? valueKey : 'option';
              return (
                <StyledAgentChatConfirmationOption
                  variant={variant}
                  key={value}
                  onClick={() => isMultiple ? toggleOption(value) : handleSend(value)}
                >
                  <Space>
                    {['confirm', 'submit'].includes(valueKey) ? <CheckIcon /> : valueKey === 'cancel' ? <CloseIcon /> : null}
                    {optionsType === 'multiple' && (
                      <Checkbox checked={selectedOptions.includes(value)} />
                    )}
                    <span>{label}</span>
                  </Space>
                </StyledAgentChatConfirmationOption>
            )})}
          </Space>

          {(isMultiple || isOptional) && (
            <Space size='middle'>
              {isMultiple && (
                <StyledAgentChatOptionButton
                  onClick={() => submitOptions(selectedOptions)}
                  disabled={selectedOptions.length === 0}
                  type='link'
                >
                  {t('submit')}
                </StyledAgentChatOptionButton>
              )}
              {isOptional && (
                <StyledAgentChatOptionButton
                  onClick={() => handleSend(`Skip ${fieldName}`)}
                  type='link'
                >
                  {t('skip')}
                </StyledAgentChatOptionButton>
              )}
            </Space>
          )}
        </Space>

      </StyledAgentChatConfirmation>
    );
  }

  // Formats a chat message based on its type
  const formatMessage = ({ id, file, message, type = 'text', options, role }, idx, allMessages) => {
    // Error message
    if (type === 'error') {
      return <Alert type='error' showIcon message={message || 'An error occurred.'} />;
    }
    // File attachment
    if (type === 'file') {
      return <Attachments.FileCard key={id} item={file} />;
    }
    // Only display options if this is the latest message
    if (role === 'assistant' && idx !== allMessages.length - 1 && options) {
      return (
        <StyledAgentChatConfirmation>
          <p>{parseMessage(message)}</p>
        </StyledAgentChatConfirmation>
      )
    }
    // Confirmation with options (only if latest message)
    if (type === 'options' && options) {
      return renderOptions({message, options});
    }
    if (typeof message === 'string') {
      return <div>{parseMessage(message)}</div>;
    }
    // Fallback for unknown types or missing message
    return null;
  }

  return (
    <StyledAgentChatDrawer
      title='Agent Chat'
      placement='right'
      width={480}
      onClose={() => onClose?.()}
      open={open}
      destroyOnClose
      {...rest}
    >
      {!socketUrl ? (
        <Empty description={t('agentChat:errors.serverIsNotConfigured')} />
      ) : (
        <StyledAgentChat>
          <StyledAgentChatBody ref={chatBodyRef} align='start' direction='vertical'>
            <StyledAgentChatBodyWelcome size='small' direction='vertical'>
              <StyledAgentChatBodyWelcomeTitle>
                <Space size='small' direction='horizontal'>
                  <StarsIcon />
                  <span>{t('hi')} {name},</span>
                </Space>
              </StyledAgentChatBodyWelcomeTitle>
            </StyledAgentChatBodyWelcome>

            {/* Messages */}
            <AnimatePresence initial={false}>
              <Space style={{width: '100%'}} direction='vertical' size='middle'>
                {messages.map((message, idx) => (
                  <ScaleFade key={idx}>
                    <Bubble
                      className={message.type && `bubble-${message.type}-message`}
                      content={formatMessage(message, idx, messages)}
                      role={message?.role}
                      key={message?.id}
                      placement={roles[message?.role]?.placement || 'start'}
                    />
                  </ScaleFade>
                ))}
              </Space>
            </AnimatePresence>

            <StyledAgentChatWaitingSpaceContainer>
              {loading && getWaitingSpinner('small')}
            </StyledAgentChatWaitingSpaceContainer>

            {error && (
              <Alert type='error' showIcon message={error} />
            )}
          </StyledAgentChatBody>

          <StyledAgentChatFooter
            direction='vertical'
            size='small'
            className='w-100'
          >
            {footerHelper}
            <Sender
              ref={senderRef}
              disabled={isInputDisabled}
              value={input}
              onChange={setInput}
              autoSize={{ minRows: 1, maxRows: 6 }}
              placeholder={t('pleaseEnterToSendAMessage')}
              footer={({ components }) => {
                const { SendButton } = components;
                return (
                  <Flex justify='space-between' align='center'>
                    <Flex align='center'>
                      <Button
                        disabled={isInputDisabled}
                        type='text'
                        icon={<PlusOutlined />}
                        shape='circle'
                        onClick={handlePlusClick}
                      />
                      <input
                        className='d-none'
                        ref={fileInputRef}
                        type='file'
                        onChange={handleFileChange}
                        disabled={isInputDisabled}
                      />
                    </Flex>
                    <Flex align='center'>
                      <SendButton type='text' icon={<SendOutlined />} />
                    </Flex>
                  </Flex>
                );
              }}
              onSubmit={onSubmit}
              actions={false}
            />
          </StyledAgentChatFooter>
        </StyledAgentChat>
      )}
    </StyledAgentChatDrawer>
  );
});

AgentChat.propTypes = {
  flow: PropTypes.oneOf(Object.values(agentWebsocketFlowConstants)).isRequired,
  onUserMessage: PropTypes.func,
  query: PropTypes.object,
}

export default AgentChat;
